import {
  DoBoxSpecialBucketKey,
  InboxEntry,
  InboxEntryNewReply,
  InboxEntryOrderKeyAssignment,
  InboxEntryReplyStatePayload,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import { dropNewReplyMessageIds } from '@this+that/utils';

import {
  ActiveTaskSubgroup,
  BucketKey,
  BucketPaginationState,
  DismissedRepliesSnapshot,
  HandlerGet,
  HandlerSet,
  InboxEntriesHandlerSlice,
} from './types';
import { InboxEntrySaveOrigin } from '../../../enums/inboxEntrySaveOrigin';
import type { DoBoxViewFilters } from '../../../pages/private/DoBox/utils/matchesDoBoxViewFilters';
import { resolveViewBucketKeyForEntry } from '../../../pages/private/DoBox/utils/resolveBucketKeyForEntry';
import { DoBoxBucketEntriesResult } from '../../../services/inboxEntries.service';
import { CONFIRMATION_CLOCK_SKEW_MS } from '../../../utils/constants/drafts';
import { mergeTicketArtifacts } from '../../../utils/delegation/mergeTicketArtifacts';
import {
  clearDiscounts,
  recordDiscount,
} from '../../../utils/inboxEntryDiscountLedger';
import { getOptimisticMove } from '../../../utils/optimisticMoveGuard';
import {
  buildThreadRestoreUserCommunication,
  isEmailCommunication,
  isFreshUnconfirmedDraft,
  resolveServerRowDraftId,
  rowCarriesDraft,
  stripThreadCarriedDraft,
} from '../../../utils/userCommunicationHelpers';
import { useDoBoxFilterSortStore } from '../../DoBox/useDoBoxFilterSortStore';
import type { UserCommunicationsStore } from '../UserCommunications/types';
import { useUserCommunicationsStore } from '../UserCommunications/useUserCommunicationsStore';

// Read-only cross-store access (NOT a hook — this runs inside zustand updaters,
// outside React render, where hooks throw). Mirrors the pattern already used by
// useDoBoxFilterSortStore itself for useTeamStore.
export const getCurrentDoBoxViewFilters = (): DoBoxViewFilters =>
  useDoBoxFilterSortStore.getState();

// The single place bucketTotals is ever mutated outside a full server refetch
// (handleSaveInitialDoBoxBuckets). A bucket key absent from bucketTotals means
// its total is unknown (unreadOnly filter, or not yet fetched) — never invented.
export const applyBucketTotalDelta = (
  bucketTotals: Record<BucketKey, number>,
  bucketKey: BucketKey | null,
  delta: 1 | -1,
): Record<BucketKey, number> => {
  if (!bucketKey || !(bucketKey in bucketTotals)) {
    return bucketTotals;
  }
  const nextValue = Math.max(0, bucketTotals[bucketKey] + delta);
  if (nextValue === bucketTotals[bucketKey]) {
    return bucketTotals;
  }
  return { ...bucketTotals, [bucketKey]: nextValue };
};

// before/after are the entry's view bucket (see resolveViewBucketKeyForEntry)
// pre- and post-mutation; null means "not in the current view" (e.g. resolved
// while filtering to unresolved). A no-op transition (before === after,
// including null === null) short-circuits to avoid a needless object clone.
export const applyBucketTotalsTransition = (
  bucketTotals: Record<BucketKey, number>,
  before: BucketKey | null,
  after: BucketKey | null,
): Record<BucketKey, number> => {
  if (before === after) {
    return bucketTotals;
  }
  return applyBucketTotalDelta(
    applyBucketTotalDelta(bucketTotals, before, -1),
    after,
    1,
  );
};

export const rebuildTaskSubgroupMembership = (
  subgroups: ActiveTaskSubgroup[],
  entries: InboxEntry[],
): ActiveTaskSubgroup[] => {
  const idsBySubgroup = new Map<string, Set<string>>(
    subgroups.map((sg) => [sg.id, new Set<string>()]),
  );
  for (const entry of entries) {
    const sgId = entry.taskSubgroupId;
    if (sgId && idsBySubgroup.has(sgId)) {
      idsBySubgroup.get(sgId)!.add(entry.id);
    }
  }
  return subgroups.map((sg) => ({
    ...sg,
    inboxEntryIds: idsBySubgroup.get(sg.id) ?? new Set<string>(),
  }));
};

type ZustandSet = HandlerSet;
type ZustandGet = HandlerGet;

// A top-up loadMore right after a move can re-fetch a card already in state
// via a stale cursor; keep the existing (already-moved) entry, drop the duplicate.
const appendWithoutDuplicates = (
  existing: InboxEntry[],
  incoming: InboxEntry[],
): InboxEntry[] => {
  const existingIds = new Set(existing.map((entry) => entry.id));
  return [
    ...existing,
    ...incoming.filter((entry) => !existingIds.has(entry.id)),
  ];
};

// A subsequent save of an already-present draft updates it in place (matched
// by draftId). A brand-new reply/forward draft becomes its thread's head row
// in place instead — the same row a reload would show post-ingestion — found
// by threadMessageId, or by messageId for the repliedTo row (see
// resolveDraftThreadMessageId, useSmartDraftManager.ts). A brand-new compose
// has neither and simply prepends. See STORE.md "InboxEntries — drafts
// inside a task".
const upsertThreadHeadCommunication = (
  communications: Partial<UserCommunication>[],
  draftCommunication: Partial<UserCommunication>,
  messageId: string | undefined,
): Partial<UserCommunication>[] => {
  const draftId = draftCommunication.draftId;
  const existingDraftIndex = draftId
    ? communications.findIndex((comm) => comm.draftId === draftId)
    : -1;

  if (existingDraftIndex !== -1) {
    return communications.map((comm, index) =>
      index === existingDraftIndex ? { ...comm, ...draftCommunication } : comm,
    );
  }

  const threadIndex = communications.findIndex(
    (comm) =>
      (!!draftCommunication.threadMessageId &&
        comm.threadMessageId === draftCommunication.threadMessageId) ||
      (!!messageId && comm.messageId === messageId),
  );

  if (threadIndex !== -1) {
    return communications.map((comm, index) =>
      index === threadIndex ? draftCommunication : comm,
    );
  }

  return [draftCommunication, ...communications];
};

const buildCommunicationIds = (
  communications: Partial<UserCommunication>[],
): string[] =>
  communications
    .map((c) => c.messageId || c.draftId)
    .filter(Boolean) as string[];

const buildCommunicationUpdater =
  (userCommunication: Partial<UserCommunication>) =>
  (comm: Partial<UserCommunication>): Partial<UserCommunication> => {
    const matchByMessageId =
      userCommunication.messageId &&
      comm.messageId === userCommunication.messageId;
    const matchByDraftId =
      userCommunication.draftId && comm.draftId === userCommunication.draftId;
    return matchByMessageId || matchByDraftId
      ? { ...comm, ...userCommunication }
      : comm;
  };

const filterCommunicationsByMessageIds = (
  communications: Partial<UserCommunication>[] | undefined,
  messageIdsSet: Set<string>,
): Partial<UserCommunication>[] =>
  (communications ?? []).filter((comm) => !messageIdsSet.has(comm.messageId!));

const applyLabelUpdateToCommunications = (
  communications: Partial<UserCommunication>[] | undefined,
  messageIds: string[],
  updateLabels: (currentLabels: LabelId[] | undefined) => LabelId[],
): Partial<UserCommunication>[] =>
  (communications ?? []).map((comm) =>
    messageIds.includes(comm.messageId!)
      ? { ...comm, labelIds: updateLabels(comm.labelIds) }
      : comm,
  );

export const handleSetLoading = async (set: ZustandSet, isLoading: boolean) => {
  set({ isLoading });
};

export const handleSetCommunicationsLoading = async (
  set: ZustandSet,
  communicationsLoading: boolean,
) => {
  set({ communicationsLoading });
};

export const handleSetBucketLoading = (
  set: ZustandSet,
  bucketKey: BucketKey,
  isLoading: boolean,
) => {
  set((state) => {
    const existing = state.bucketPagination[bucketKey] ?? {
      startKey: null,
      hasMore: false,
    };
    return {
      bucketPagination: {
        ...state.bucketPagination,
        [bucketKey]: { ...existing, isLoading },
      },
    };
  });
};

// A local removal that lowered a bucket total is remembered, so its own echo
// is not counted again when it reaches an entry this tab no longer holds.
const discountRemovedEntry = (
  entryId: string,
  before: Record<BucketKey, number>,
  after: Record<BucketKey, number>,
): void => {
  if (after !== before) {
    recordDiscount(entryId);
  }
};

// For a WS REMOVE/hard-delete of an entry never loaded locally (so no
// before/after transition is possible — see resolveViewBucketKeyForEntry
// call sites in inboxEntriesSync.ts): applies the delta directly from the
// event payload's own classification, keeping the "bucketTotals mutates only
// in store handlers" invariant.
export const handleAdjustBucketTotal = (
  set: ZustandSet,
  bucketKey: BucketKey | null,
  delta: 1 | -1,
) => {
  set((state) => ({
    bucketTotals: applyBucketTotalDelta(state.bucketTotals, bucketKey, delta),
  }));
};

const isEntryInBucket = (entry: InboxEntry, bucketKey: BucketKey): boolean =>
  bucketKey === DoBoxSpecialBucketKey.BACKLOG
    ? !entry.taskSubgroupId
    : entry.taskSubgroupId === bucketKey;

// The unconfirmed-drafts ledger is the fallback source for a task card's
// local draft when a bucket refresh (handleSaveInitialDoBoxBuckets) already
// wiped the entry's own `communications` before this reconciliation runs —
// see STORE.md "InboxEntries — drafts inside a task". Ledger-driven
// rather than a category scan: the ledger is tiny, and getRichestUserCommunicationByDraftId
// resolves a row regardless of which view calculateViews routed it to, and — unlike
// the first-match getter — never returns a thread-less echo from `all` when a
// richer copy (with threadMessages) exists in another category.
const collectFreshUnconfirmedDraftRowsForEntry = (
  ucSnapshot: Pick<
    UserCommunicationsStore,
    'unconfirmedDrafts' | 'getRichestUserCommunicationByDraftId'
  >,
  inboxEntryId: string,
  now: number,
): UserCommunication[] => {
  const ledgerKeys = Object.keys(ucSnapshot.unconfirmedDrafts);
  const freshDraftIds = ledgerKeys.filter((draftId) =>
    isFreshUnconfirmedDraft(ucSnapshot.unconfirmedDrafts, { draftId }, now),
  );
  const resolvedRows = freshDraftIds
    .map((draftId) => ucSnapshot.getRichestUserCommunicationByDraftId(draftId))
    .filter((row): row is UserCommunication => !!row);
  const keptRows = resolvedRows.filter((row) =>
    row.inboxEntryIds?.includes(inboxEntryId),
  );

  return keptRows;
};

// previousLocal wins over the UC row for the same draftId — it's the shape
// the card was already rendering.
const collectCandidateDraftRows = (
  previousLocal: Partial<UserCommunication>[] | undefined,
  ucDraftRowsForEntry: UserCommunication[],
): Map<string, Partial<UserCommunication>> => {
  const rows = new Map<string, Partial<UserCommunication>>();
  ucDraftRowsForEntry.forEach((row) => {
    const draftId = resolveServerRowDraftId(row);
    if (draftId) {
      rows.set(draftId, row);
    }
  });
  (previousLocal ?? []).forEach((row) => {
    if (row.draftId) {
      rows.set(row.draftId, row);
    }
  });
  return rows;
};

const isNestedDraftCarrier = (
  row: Partial<UserCommunication>,
  draftId: string,
): boolean => row.draftId !== draftId && rowCarriesDraft(row, draftId);

const findTopLevelDraftIndex = (
  incoming: Partial<UserCommunication>[],
  draftId: string,
): number =>
  incoming.findIndex(
    (row) => row.draftId === draftId || row.messageId === draftId,
  );

// A candidate draft row's thread — matched by threadMessageId (or by the
// original's own messageId, for a thread whose head had no threadMessageId
// yet) — identifies the incoming row still representing the pre-draft
// original, the case a server refetch returns before ingestion completes.
const findThreadRowIndex = (
  incoming: Partial<UserCommunication>[],
  localRow: Partial<UserCommunication>,
): number => {
  if (!localRow.threadMessageId) {
    return -1;
  }
  return incoming.findIndex(
    (row) =>
      row.threadMessageId === localRow.threadMessageId ||
      row.messageId === localRow.threadMessageId,
  );
};

// Reconciles one candidate draftId against the incoming server payload for a
// single entry slot (list or modal) — see STORE.md "InboxEntries — drafts
// inside a task" for the rule table this implements.
const reconcileDraftCandidate = (
  draftId: string,
  localRow: Partial<UserCommunication>,
  incoming: Partial<UserCommunication>[],
  unconfirmedDrafts: Record<string, number>,
  now: number,
): Partial<UserCommunication>[] => {
  if (incoming.some((row) => isNestedDraftCarrier(row, draftId))) {
    return incoming;
  }

  const isFresh = isFreshUnconfirmedDraft(unconfirmedDrafts, localRow, now);
  const topLevelIndex = findTopLevelDraftIndex(incoming, draftId);

  if (topLevelIndex !== -1) {
    if (!isFresh) {
      return incoming;
    }
    const serverIsNewer =
      (incoming[topLevelIndex].createdAt ?? 0) >
      unconfirmedDrafts[draftId] + CONFIRMATION_CLOCK_SKEW_MS;
    return serverIsNewer
      ? incoming
      : incoming.map((row, index) =>
          index === topLevelIndex ? localRow : row,
        );
  }

  if (!isFresh) {
    return incoming;
  }

  const threadIndex = findThreadRowIndex(incoming, localRow);
  if (threadIndex !== -1) {
    return incoming.map((row, index) =>
      index === threadIndex ? localRow : row,
    );
  }

  return [localRow, ...incoming];
};

interface ReconcileEntryCommunicationsParams {
  incoming: Partial<UserCommunication>[];
  previousLocal: Partial<UserCommunication>[] | undefined;
  unconfirmedDrafts: Record<string, number>;
  ucDraftRowsForEntry: UserCommunication[];
  now: number;
}

// A draft saved locally has no server WebSocket confirmation (see STORE.md
// "UserCommunications — unconfirmed drafts"), so a wholesale communications
// replacement (detail fetch, WS MODIFY, bucket refresh) must reconcile
// rather than overwrite. See STORE.md "InboxEntries — drafts inside a task"
// for the rule table this implements.
export const reconcileEntryCommunications = ({
  incoming,
  previousLocal,
  unconfirmedDrafts,
  ucDraftRowsForEntry,
  now,
}: ReconcileEntryCommunicationsParams): Partial<UserCommunication>[] => {
  const candidateRows = collectCandidateDraftRows(
    previousLocal,
    ucDraftRowsForEntry,
  );

  if (candidateRows.size === 0) {
    return incoming;
  }

  let communications = incoming;

  candidateRows.forEach((localRow, draftId) => {
    communications = reconcileDraftCandidate(
      draftId,
      localRow,
      communications,
      unconfirmedDrafts,
      now,
    );
  });

  return communications;
};

export const handleSaveCommunicationsInInboxEntry = async (
  set: ZustandSet,
  inboxEntryId: string,
  communications: UserCommunication[],
) => {
  const now = Date.now();
  const ucSnapshot = useUserCommunicationsStore.getState();
  const ucDraftRowsForEntry = collectFreshUnconfirmedDraftRowsForEntry(
    ucSnapshot,
    inboxEntryId,
    now,
  );

  set((state) => {
    const listEntry = state.inboxEntries.find(
      (entry) => entry.id === inboxEntryId,
    );
    const modalMatches = state.modalInboxEntry?.id === inboxEntryId;

    const reconcileSlot = (
      previousLocal: Partial<UserCommunication>[] | undefined,
    ) =>
      reconcileEntryCommunications({
        incoming: communications,
        previousLocal,
        unconfirmedDrafts: ucSnapshot.unconfirmedDrafts,
        ucDraftRowsForEntry,
        now,
      });

    const listCommunications = listEntry
      ? reconcileSlot(listEntry.communications)
      : communications;
    const modalCommunications = modalMatches
      ? reconcileSlot(state.modalInboxEntry!.communications)
      : communications;

    return {
      inboxEntries: state.inboxEntries.map((inboxEntry) =>
        inboxEntry.id === inboxEntryId
          ? {
              ...inboxEntry,
              communications: listCommunications as UserCommunication[],
            }
          : inboxEntry,
      ),
      modalInboxEntry: modalMatches
        ? {
            ...state.modalInboxEntry!,
            communications: modalCommunications as UserCommunication[],
          }
        : state.modalInboxEntry,
    };
  });
};

// A late-resolving fetch (e.g. a detail/checklist response for an entry that
// was just moved to another task group) must not resurrect that entry into
// the active group's list — it belongs elsewhere now. Only compares when both
// ids are known; a null/undefined taskGroupId (e.g. in existing unit fixtures)
// never triggers the guard.
const belongsToAnotherTaskGroup = (
  entryTaskGroupId: string | null | undefined,
  activeTaskGroupId: string | null,
): boolean =>
  entryTaskGroupId != null &&
  activeTaskGroupId != null &&
  entryTaskGroupId !== activeTaskGroupId;

const mergeDetailedEntryWithExisting = (
  detailedEntry: InboxEntry,
  existingEntry: InboxEntry,
): InboxEntry => ({
  ...detailedEntry,
  communications:
    detailedEntry.communications !== undefined
      ? detailedEntry.communications
      : existingEntry.communications,
  entryKind:
    detailedEntry.entryKind !== undefined
      ? detailedEntry.entryKind
      : existingEntry.entryKind,
  flowApproval:
    detailedEntry.flowApproval !== undefined
      ? detailedEntry.flowApproval
      : existingEntry.flowApproval,
  isSuggestion:
    detailedEntry.isSuggestion !== undefined
      ? detailedEntry.isSuggestion
      : existingEntry.isSuggestion,
  relatedInboxEntries:
    detailedEntry.relatedInboxEntries !== undefined
      ? detailedEntry.relatedInboxEntries
      : existingEntry.relatedInboxEntries,
  newReplies:
    detailedEntry.newReplies !== undefined
      ? detailedEntry.newReplies
      : existingEntry.newReplies,
  chatActivity:
    detailedEntry.chatActivity !== undefined
      ? detailedEntry.chatActivity
      : existingEntry.chatActivity,
  agentRunHistory:
    detailedEntry.agentRunHistory !== undefined
      ? detailedEntry.agentRunHistory
      : existingEntry.agentRunHistory,
  ticketArtifacts: mergeTicketArtifacts(
    detailedEntry.ticketArtifacts,
    existingEntry.ticketArtifacts,
  ),
  originFlow:
    detailedEntry.originFlow !== undefined
      ? detailedEntry.originFlow
      : existingEntry.originFlow,
});

// Entry-level reconciliation for a wholesale server row (detail fetch, bucket
// refresh, per-bucket list): carries over the fields mergeDetailedEntryWithExisting
// already protects, then — only when the incoming row actually carries a
// communications array — reconciles it against the previous local entry's
// communications through the same rule table as every other save path. See
// STORE.md "InboxEntries — drafts inside a task": a bucket refetch racing a
// draft's server ingestion must not silently drop the local draft head.
export const reconcileIncomingEntry = (
  incomingEntry: InboxEntry,
  previousLocal: InboxEntry | undefined,
  ucSnapshot: Pick<
    UserCommunicationsStore,
    'unconfirmedDrafts' | 'getRichestUserCommunicationByDraftId'
  >,
  now: number,
): InboxEntry => {
  const base = previousLocal
    ? mergeDetailedEntryWithExisting(incomingEntry, previousLocal)
    : incomingEntry;

  if (!Array.isArray(incomingEntry.communications)) {
    return base;
  }

  const ucDraftRowsForEntry = collectFreshUnconfirmedDraftRowsForEntry(
    ucSnapshot,
    incomingEntry.id,
    now,
  );

  return {
    ...base,
    communications: reconcileEntryCommunications({
      incoming: incomingEntry.communications as Partial<UserCommunication>[],
      previousLocal: previousLocal?.communications,
      unconfirmedDrafts: ucSnapshot.unconfirmedDrafts,
      ucDraftRowsForEntry,
      now,
    }) as UserCommunication[],
  };
};

// The modal slot can be the very same object as its list entry, a different
// deep-linked object (a resolved entry not present in the list), or an id the
// incoming payload never touched at all — mirrors the three-way branch
// handleSaveDetailedInboxEntry resolves for its own modal slot, generalized to
// an array of incoming rows.
const reconcileModalSlotForIncoming = (
  modalInboxEntry: InboxEntry | null,
  previousById: Map<string, InboxEntry>,
  incomingRaw: InboxEntry[],
  reconciledIncoming: InboxEntry[],
  ucSnapshot: Pick<
    UserCommunicationsStore,
    'unconfirmedDrafts' | 'getRichestUserCommunicationByDraftId'
  >,
  now: number,
): InboxEntry | null => {
  if (!modalInboxEntry) {
    return modalInboxEntry;
  }
  const rawMatch = incomingRaw.find((entry) => entry.id === modalInboxEntry.id);
  if (!rawMatch) {
    return modalInboxEntry;
  }
  if (modalInboxEntry === previousById.get(modalInboxEntry.id)) {
    return (
      reconciledIncoming.find((entry) => entry.id === modalInboxEntry.id) ??
      modalInboxEntry
    );
  }
  return reconcileIncomingEntry(rawMatch, modalInboxEntry, ucSnapshot, now);
};

export const handleSaveInboxEntries = async (
  set: ZustandSet,
  inboxEntries: InboxEntry[],
  exclusiveStartKey: string | null,
  loadMore?: boolean,
  bucketKey?: BucketKey,
) => {
  if (!bucketKey) {
    return;
  }

  const now = Date.now();
  const ucSnapshot = useUserCommunicationsStore.getState();

  set((state) => {
    const previousById = new Map(
      state.inboxEntries.map((entry) => [entry.id, entry]),
    );
    const reconciledIncoming = inboxEntries.map((entry) =>
      reconcileIncomingEntry(
        entry,
        previousById.get(entry.id),
        ucSnapshot,
        now,
      ),
    );

    return {
      inboxEntries: loadMore
        ? appendWithoutDuplicates(state.inboxEntries, reconciledIncoming)
        : [
            ...state.inboxEntries.filter(
              (entry) => !isEntryInBucket(entry, bucketKey),
            ),
            ...reconciledIncoming,
          ],
      bucketPagination: {
        ...state.bucketPagination,
        [bucketKey]: {
          startKey: exclusiveStartKey,
          hasMore: Boolean(exclusiveStartKey),
          isLoading: false,
        },
      },
      modalInboxEntry: reconcileModalSlotForIncoming(
        state.modalInboxEntry,
        previousById,
        inboxEntries,
        reconciledIncoming,
        ucSnapshot,
        now,
      ),
    };
  });
};

// The backend returns a totalCount for every bucket in one sweep (undefined
// only when filters.unreadOnly skips it), so — unlike bucketPagination, whose
// merge path spreads onto the previous state — the fresh set wholesale-replaces
// bucketTotals every time: a stale total from before unreadOnly was toggled on
// must not linger as if it were still valid for the new view.
const buildBucketTotalsFromResults = (
  results: DoBoxBucketEntriesResult[],
): Record<BucketKey, number> =>
  results.reduce<Record<BucketKey, number>>((acc, result) => {
    if (result.totalCount != null) {
      acc[result.bucketKey] = result.totalCount;
    }
    return acc;
  }, {});

const dedupeEntriesById = (entries: InboxEntry[]): InboxEntry[] => {
  const byId = new Map<string, InboxEntry>();
  for (const entry of entries) {
    byId.set(entry.id, entry);
  }
  return Array.from(byId.values());
};

const shiftTotalsForPreservedPlacements = (
  totals: Record<BucketKey, number>,
  fetched: InboxEntry[],
  preserved: InboxEntry[],
  viewFilters: DoBoxViewFilters,
): Record<BucketKey, number> =>
  fetched.reduce(
    (acc, entry, index) =>
      preserved[index] === entry
        ? acc
        : applyBucketTotalsTransition(
            acc,
            resolveViewBucketKeyForEntry(entry, viewFilters),
            resolveViewBucketKeyForEntry(preserved[index], viewFilters),
          ),
    totals,
  );

const preserveNewerPlacement = (
  incoming: InboxEntry,
  local: InboxEntry | undefined,
): InboxEntry => {
  const guard = getOptimisticMove(incoming.id);
  if (guard) {
    return {
      ...incoming,
      taskSubgroupId: guard.taskSubgroupId,
      orderKey: guard.orderKey ?? incoming.orderKey,
    };
  }
  if (
    local?.version != null &&
    incoming.version != null &&
    local.version > incoming.version
  ) {
    return {
      ...incoming,
      taskSubgroupId: local.taskSubgroupId,
      orderKey: local.orderKey,
    };
  }
  return incoming;
};

export const handleSaveInitialDoBoxBuckets = async (
  set: ZustandSet,
  results: DoBoxBucketEntriesResult[],
  merge = false,
) => {
  const now = Date.now();
  const ucSnapshot = useUserCommunicationsStore.getState();

  clearDiscounts();
  set((state) => {
    const previousById = new Map(
      state.inboxEntries.map((entry) => [entry.id, entry]),
    );
    const fetched = dedupeEntriesById(
      results.flatMap((result) => result.inboxEntries),
    );
    const incomingRaw = fetched.map((entry) =>
      preserveNewerPlacement(entry, previousById.get(entry.id)),
    );
    const bucketPaginationFromResults = results.reduce<
      Record<BucketKey, BucketPaginationState>
    >((acc, result) => {
      acc[result.bucketKey] = {
        startKey: result.exclusiveStartKey,
        hasMore: Boolean(result.exclusiveStartKey),
        isLoading: false,
      };
      return acc;
    }, {});
    const bucketTotals = shiftTotalsForPreservedPlacements(
      buildBucketTotalsFromResults(results),
      fetched,
      incomingRaw,
      getCurrentDoBoxViewFilters(),
    );

    const incoming = incomingRaw.map((entry) =>
      reconcileIncomingEntry(
        entry,
        previousById.get(entry.id),
        ucSnapshot,
        now,
      ),
    );
    const modalInboxEntry = reconcileModalSlotForIncoming(
      state.modalInboxEntry,
      previousById,
      incomingRaw,
      incoming,
      ucSnapshot,
      now,
    );

    if (!merge) {
      return {
        inboxEntries: incoming,
        bucketPagination: bucketPaginationFromResults,
        bucketTotals,
        modalInboxEntry,
      };
    }

    // Sort re-fetch: keep on-screen cards so a re-sorted card doesn't vanish after moving; sortedEntries re-orders the union.
    const incomingIds = new Set(incoming.map((entry) => entry.id));
    const retained = state.inboxEntries.filter(
      (entry) => !incomingIds.has(entry.id),
    );

    return {
      inboxEntries: [...retained, ...incoming],
      bucketPagination: {
        ...state.bucketPagination,
        ...bucketPaginationFromResults,
      },
      bucketTotals,
      modalInboxEntry,
    };
  });
};

const carryOverUnsentFields = (
  updated: InboxEntry,
  base: InboxEntry,
): InboxEntry => ({
  ...updated,
  communications: updated.communications ?? base.communications,
  relatedInboxEntries: updated.relatedInboxEntries ?? base.relatedInboxEntries,
  newReplies: updated.newReplies ?? base.newReplies,
  chatActivity: updated.chatActivity ?? base.chatActivity,
  agentRunHistory: updated.agentRunHistory ?? base.agentRunHistory,
  ticketArtifacts: mergeTicketArtifacts(
    updated.ticketArtifacts,
    base.ticketArtifacts,
  ),
  originFlow:
    updated.originFlow !== undefined ? updated.originFlow : base.originFlow,
  taskSubgroupId:
    updated.taskSubgroupId !== undefined
      ? updated.taskSubgroupId
      : base.taskSubgroupId,
});

export const handleUpdateInboxEntry = async (
  set: ZustandSet,
  get: ZustandGet,
  updatedEntry: InboxEntry,
) => {
  const activeTaskGroupId = get().taskGroupId;
  const shouldDropFromList = belongsToAnotherTaskGroup(
    updatedEntry.taskGroupId,
    activeTaskGroupId,
  );
  const viewFilters = getCurrentDoBoxViewFilters();

  set((state) => {
    const existingEntry = state.inboxEntries.find(
      (entry) => entry.id === updatedEntry.id,
    );
    const mergedEntry = existingEntry
      ? carryOverUnsentFields(updatedEntry, existingEntry)
      : null;

    const beforeBucketKey = existingEntry
      ? resolveViewBucketKeyForEntry(existingEntry, viewFilters)
      : null;
    const afterBucketKey =
      existingEntry && !shouldDropFromList
        ? resolveViewBucketKeyForEntry(mergedEntry!, viewFilters)
        : null;

    const mergedModal =
      state.modalInboxEntry?.id === updatedEntry.id
        ? carryOverUnsentFields(updatedEntry, state.modalInboxEntry)
        : state.modalInboxEntry;

    return {
      inboxEntries: shouldDropFromList
        ? state.inboxEntries.filter((entry) => entry.id !== updatedEntry.id)
        : state.inboxEntries.map((inboxEntry) =>
            inboxEntry.id === updatedEntry.id ? mergedEntry! : inboxEntry,
          ),
      modalInboxEntry: mergedModal,
      bucketTotals: existingEntry
        ? applyBucketTotalsTransition(
            state.bucketTotals,
            beforeBucketKey,
            afterBucketKey,
          )
        : state.bucketTotals,
    };
  });
};

export const handleUpdateInboxEntryFields = async (
  set: ZustandSet,
  inboxEntryId: string,
  fields: Partial<InboxEntry>,
) => {
  const viewFilters = getCurrentDoBoxViewFilters();

  set((state) => {
    const existingEntry = state.inboxEntries.find(
      (entry) => entry.id === inboxEntryId,
    );
    const beforeBucketKey = existingEntry
      ? resolveViewBucketKeyForEntry(existingEntry, viewFilters)
      : null;
    const afterBucketKey = existingEntry
      ? resolveViewBucketKeyForEntry(
          { ...existingEntry, ...fields },
          viewFilters,
        )
      : null;

    return {
      inboxEntries: state.inboxEntries.map((inboxEntry) =>
        inboxEntry.id === inboxEntryId
          ? { ...inboxEntry, ...fields }
          : inboxEntry,
      ),
      modalInboxEntry:
        state.modalInboxEntry?.id === inboxEntryId
          ? { ...state.modalInboxEntry, ...fields }
          : state.modalInboxEntry,
      bucketTotals: existingEntry
        ? applyBucketTotalsTransition(
            state.bucketTotals,
            beforeBucketKey,
            afterBucketKey,
          )
        : state.bucketTotals,
    };
  });
};

// assignments only ever carry {inboxEntryId, orderKey} — taskSubgroupId moves
// go through assignInboxEntryToSubgroup instead — so no entry ever changes
// bucket here and bucketTotals is intentionally left untouched.
export const handleSetInboxEntryOrderKeys = async (
  set: ZustandSet,
  assignments: InboxEntryOrderKeyAssignment[],
) => {
  const orderKeyByEntryId = new Map(
    assignments.map((assignment) => [
      assignment.inboxEntryId,
      assignment.orderKey,
    ]),
  );

  set((state) => {
    const modalOrderKey = state.modalInboxEntry
      ? orderKeyByEntryId.get(state.modalInboxEntry.id)
      : undefined;

    return {
      inboxEntries: state.inboxEntries.map((inboxEntry) => {
        const orderKey = orderKeyByEntryId.get(inboxEntry.id);
        return orderKey !== undefined
          ? { ...inboxEntry, orderKey }
          : inboxEntry;
      }),
      modalInboxEntry:
        modalOrderKey !== undefined && state.modalInboxEntry
          ? { ...state.modalInboxEntry, orderKey: modalOrderKey }
          : state.modalInboxEntry,
    };
  });
};

const resolveSaveBucketKeys = (
  existingEntry: InboxEntry | null,
  mergedEntry: InboxEntry,
  shouldDropFromList: boolean,
  origin: InboxEntrySaveOrigin,
  viewFilters: DoBoxViewFilters,
): { before: BucketKey | null; after: BucketKey | null } => {
  const after = shouldDropFromList
    ? null
    : resolveViewBucketKeyForEntry(mergedEntry, viewFilters);
  if (existingEntry) {
    return {
      before: resolveViewBucketKeyForEntry(existingEntry, viewFilters),
      after,
    };
  }
  // A fetched entry the list never held is already in the server's total.
  return {
    before: origin === InboxEntrySaveOrigin.ALREADY_COUNTED ? after : null,
    after,
  };
};

export const handleSaveDetailedInboxEntry = async (
  set: ZustandSet,
  get: ZustandGet,
  detailedEntry: InboxEntry,
  origin: InboxEntrySaveOrigin = InboxEntrySaveOrigin.NEW_TO_TOTAL,
) => {
  const activeTaskGroupId = get().taskGroupId;
  const shouldDropFromList =
    belongsToAnotherTaskGroup(detailedEntry.taskGroupId, activeTaskGroupId) ||
    detailedEntry.deletedAt != null;
  const viewFilters = getCurrentDoBoxViewFilters();
  const now = Date.now();
  const ucSnapshot = useUserCommunicationsStore.getState();

  set((state) => {
    const existingEntryIndex = state.inboxEntries.findIndex(
      (entry) => entry.id === detailedEntry.id,
    );
    const existingEntry =
      existingEntryIndex >= 0 ? state.inboxEntries[existingEntryIndex] : null;
    const modalMatches = state.modalInboxEntry?.id === detailedEntry.id;

    const mergedEntry = reconcileIncomingEntry(
      detailedEntry,
      existingEntry ?? undefined,
      ucSnapshot,
      now,
    );

    // The modal slot can hold a different object than the list entry (a
    // deep-linked/resolved entry not in `inboxEntries`), so it's reconciled
    // against its own previous communications — except when it's literally
    // the same object as existingEntry, where mergedEntry is already correct.
    const modalMergedEntry = modalMatches
      ? state.modalInboxEntry === existingEntry
        ? mergedEntry
        : reconcileIncomingEntry(
            detailedEntry,
            state.modalInboxEntry!,
            ucSnapshot,
            now,
          )
      : null;

    const inboxEntries = shouldDropFromList
      ? state.inboxEntries.filter((entry) => entry.id !== detailedEntry.id)
      : existingEntry
        ? state.inboxEntries.map((entry, index) =>
            index === existingEntryIndex ? mergedEntry : entry,
          )
        : [mergedEntry, ...state.inboxEntries];

    const { before, after } = resolveSaveBucketKeys(
      existingEntry,
      mergedEntry,
      shouldDropFromList,
      origin,
      viewFilters,
    );
    const bucketTotals = applyBucketTotalsTransition(
      state.bucketTotals,
      before,
      after,
    );
    // A fetched drop's own removal echo is coming and must not count again.
    if (shouldDropFromList && origin === InboxEntrySaveOrigin.ALREADY_COUNTED) {
      discountRemovedEntry(detailedEntry.id, state.bucketTotals, bucketTotals);
    }

    return {
      inboxEntries,
      modalInboxEntry: modalMatches ? modalMergedEntry : state.modalInboxEntry,
      bucketTotals,
    };
  });
};

export const handleRemoveInboxEntry = async (
  set: ZustandSet,
  get: ZustandGet,
  inboxEntryId: string,
) => {
  const entryToRemove = get().inboxEntries.find(
    (entry) => entry.id === inboxEntryId,
  );
  const draftIdsToTransfer =
    entryToRemove?.communications
      ?.filter((comm) => comm.labelIds?.includes(LabelId.DRAFT))
      .map((comm) => comm.draftId || comm.messageId)
      .filter((id): id is string => Boolean(id)) || [];
  const viewFilters = getCurrentDoBoxViewFilters();
  const removedBucketKey = entryToRemove
    ? resolveViewBucketKeyForEntry(entryToRemove, viewFilters)
    : null;

  set((state) => {
    const bucketTotals = applyBucketTotalDelta(
      state.bucketTotals,
      removedBucketKey,
      -1,
    );
    discountRemovedEntry(inboxEntryId, state.bucketTotals, bucketTotals);
    return {
      inboxEntries: state.inboxEntries.filter(
        (inboxEntry) => inboxEntry.id !== inboxEntryId,
      ),
      modalInboxEntry:
        state.modalInboxEntry?.id === inboxEntryId
          ? null
          : state.modalInboxEntry,
      bucketTotals,
    };
  });

  return draftIdsToTransfer;
};

export const handleRemoveInboxEntryFromActiveList = (
  set: ZustandSet,
  updatedEntry: InboxEntry,
) => {
  const viewFilters = getCurrentDoBoxViewFilters();

  set((state) => {
    const listEntry = state.inboxEntries.find(
      (entry) => entry.id === updatedEntry.id,
    );
    const removedBucketKey = listEntry
      ? resolveViewBucketKeyForEntry(listEntry, viewFilters)
      : null;
    const heldModal =
      state.modalInboxEntry?.id === updatedEntry.id
        ? state.modalInboxEntry
        : undefined;
    const communications =
      updatedEntry.communications ??
      listEntry?.communications ??
      heldModal?.communications;
    const agentRunHistory =
      updatedEntry.agentRunHistory ??
      listEntry?.agentRunHistory ??
      heldModal?.agentRunHistory;
    const originFlow =
      updatedEntry.originFlow !== undefined
        ? updatedEntry.originFlow
        : (listEntry?.originFlow ?? heldModal?.originFlow);

    const shouldUpdateModal =
      !state.modalInboxEntry || state.modalInboxEntry.id === updatedEntry.id;

    const bucketTotals = applyBucketTotalDelta(
      state.bucketTotals,
      removedBucketKey,
      -1,
    );
    discountRemovedEntry(updatedEntry.id, state.bucketTotals, bucketTotals);

    return {
      bucketTotals,
      inboxEntries: state.inboxEntries.filter(
        (inboxEntry) => inboxEntry.id !== updatedEntry.id,
      ),
      modalInboxEntry: shouldUpdateModal
        ? { ...updatedEntry, communications, agentRunHistory, originFlow }
        : state.modalInboxEntry,
    };
  });
};

export const handleRemoveUserCommunicationsFromInboxEntries = async (
  set: ZustandSet,
  messageIds: string[],
) => {
  const messageIdsSet = new Set(messageIds);
  const viewFilters = getCurrentDoBoxViewFilters();

  set((state) => {
    const affectedEntryIds = new Set(
      state.inboxEntries
        .filter((entry) =>
          entry.communications?.some((comm) =>
            messageIdsSet.has(comm.messageId!),
          ),
        )
        .map((entry) => entry.id),
    );

    const updatedList = state.inboxEntries
      .map((inboxEntry) => {
        if (!inboxEntry.communications) {
          return inboxEntry;
        }

        const filteredCommunications = filterCommunicationsByMessageIds(
          inboxEntry.communications,
          messageIdsSet,
        ) as UserCommunication[];

        const filteredIds = filteredCommunications
          .map((comm) => comm.messageId)
          .filter((id): id is string => Boolean(id));

        return {
          ...inboxEntry,
          communications: filteredCommunications,
          communicationIds: filteredIds,
        };
      })
      .filter((inboxEntry) => {
        if (!affectedEntryIds.has(inboxEntry.id)) {
          return true;
        }

        return (inboxEntry.communications?.length ?? 0) > 0;
      });

    // An affected entry that dropped out of updatedList had its last
    // communication removed — mirrors handleRemoveInboxEntry's delta, but for
    // N entries in one batch instead of one id at a time.
    const droppedEntryIds = new Set(
      updatedList.length === state.inboxEntries.length
        ? []
        : state.inboxEntries
            .filter((entry) => affectedEntryIds.has(entry.id))
            .filter((entry) => !updatedList.some((e) => e.id === entry.id))
            .map((entry) => entry.id),
    );

    const bucketTotals =
      droppedEntryIds.size === 0
        ? state.bucketTotals
        : state.inboxEntries
            .filter((entry) => droppedEntryIds.has(entry.id))
            .reduce((acc, entry) => {
              const next = applyBucketTotalDelta(
                acc,
                resolveViewBucketKeyForEntry(entry, viewFilters),
                -1,
              );
              discountRemovedEntry(entry.id, acc, next);
              return next;
            }, state.bucketTotals);

    const modalEntry = state.modalInboxEntry;
    const modalHasMatch = modalEntry?.communications?.some((comm) =>
      messageIdsSet.has(comm.messageId!),
    );

    const updatedModalEntry = modalHasMatch
      ? (() => {
          const filteredComms = filterCommunicationsByMessageIds(
            modalEntry!.communications,
            messageIdsSet,
          );
          return {
            ...modalEntry!,
            communications: filteredComms as UserCommunication[],
            communicationIds: filteredComms
              .map((c) => c.messageId)
              .filter((id): id is string => Boolean(id)),
          };
        })()
      : modalEntry;

    return {
      inboxEntries: updatedList,
      modalInboxEntry: updatedModalEntry,
      bucketTotals,
    };
  });
};

const rewriteEntryCommunications = (
  inboxEntry: InboxEntry,
  messageIdsSet: Set<string>,
): InboxEntry => {
  if (!inboxEntry.communications) {
    return inboxEntry;
  }

  const filteredCommunications = filterCommunicationsByMessageIds(
    inboxEntry.communications,
    messageIdsSet,
  ) as UserCommunication[];

  return {
    ...inboxEntry,
    communications: filteredCommunications,
    communicationIds: buildCommunicationIds(filteredCommunications),
  };
};

// The entry survives even at zero communications: `updateInboxEntryCollections`
// only edits the link, it never deletes the entry server-side.
export const handleUnlinkUserCommunicationsFromInboxEntry = (
  set: ZustandSet,
  inboxEntryId: string,
  messageIds: string[],
) => {
  const messageIdsSet = new Set(messageIds);

  set((state) => {
    const inboxEntries = state.inboxEntries.map((inboxEntry) =>
      inboxEntry.id === inboxEntryId
        ? rewriteEntryCommunications(inboxEntry, messageIdsSet)
        : inboxEntry,
    );

    const modalInboxEntry =
      state.modalInboxEntry?.id === inboxEntryId
        ? rewriteEntryCommunications(state.modalInboxEntry, messageIdsSet)
        : state.modalInboxEntry;

    return { inboxEntries, modalInboxEntry };
  });
};

export const handleUpdateUserCommunicationLabelsInInboxEntries = (
  set: ZustandSet,
  messageIds: string[],
  updateLabels: (currentLabels: LabelId[] | undefined) => LabelId[],
) => {
  set((state) => {
    const updatedList = state.inboxEntries.map((inboxEntry) => {
      if (!inboxEntry.communications) {
        return inboxEntry;
      }

      const updatedCommunications = applyLabelUpdateToCommunications(
        inboxEntry.communications,
        messageIds,
        updateLabels,
      ) as UserCommunication[];

      return {
        ...inboxEntry,
        communications: updatedCommunications,
      };
    });

    const modalEntry = state.modalInboxEntry;
    const modalHasMatch = modalEntry?.communications?.some((comm) =>
      messageIds.includes(comm.messageId!),
    );

    const updatedModalEntry = modalHasMatch
      ? {
          ...modalEntry!,
          communications: applyLabelUpdateToCommunications(
            modalEntry!.communications,
            messageIds,
            updateLabels,
          ) as UserCommunication[],
        }
      : modalEntry;

    return {
      inboxEntries: updatedList,
      modalInboxEntry: updatedModalEntry,
    };
  });
};

const withUnread = (
  labels: LabelId[] | undefined,
  isUnread: boolean,
): LabelId[] => {
  const withoutUnread = (labels ?? []).filter(
    (label) => label !== LabelId.UNREAD,
  );
  return isUnread ? [...withoutUnread, LabelId.UNREAD] : withoutUnread;
};

type HeldEntriesMatch = string | ((entry: InboxEntry) => boolean);

const matchesHeldEntry = (match: HeldEntriesMatch, entry: InboxEntry) =>
  typeof match === 'string' ? entry.id === match : match(entry);

// The list and the modal slot hold separate copies of an entry; every transform
// that must stay consistent across both goes through this one rule.
const mapHeldEntries = (
  state: Pick<InboxEntriesHandlerSlice, 'inboxEntries' | 'modalInboxEntry'>,
  match: HeldEntriesMatch,
  transform: (entry: InboxEntry) => InboxEntry,
) => ({
  inboxEntries: state.inboxEntries.map((entry) =>
    matchesHeldEntry(match, entry) ? transform(entry) : entry,
  ),
  modalInboxEntry:
    state.modalInboxEntry && matchesHeldEntry(match, state.modalInboxEntry)
      ? transform(state.modalInboxEntry)
      : state.modalInboxEntry,
});

const setUnreadInHeldEntries = (
  set: ZustandSet,
  messageIds: string[],
  isUnread: boolean,
) => {
  set((state) =>
    mapHeldEntries(
      state,
      () => true,
      // undefined means "not loaded": [] would stop the modal and dropdown fetching them.
      (entry) =>
        entry.communications?.length
          ? {
              ...entry,
              communications: applyLabelUpdateToCommunications(
                entry.communications,
                messageIds,
                (labels) => withUnread(labels, isUnread),
              ) as UserCommunication[],
            }
          : entry,
    ),
  );
};

export const handleReadUserCommunicationsInInboxEntries = (
  set: ZustandSet,
  messageIds: string[],
) => setUnreadInHeldEntries(set, messageIds, false);

export const handleUnreadUserCommunicationsInInboxEntries = (
  set: ZustandSet,
  messageIds: string[],
) => setUnreadInHeldEntries(set, messageIds, true);

const withUnitNewReplies = (
  newReplies: InboxEntryNewReply[] | undefined,
  unitKey: string,
  messageIds: string[],
): InboxEntryNewReply[] => {
  const otherUnits = (newReplies ?? []).filter(
    (reply) => reply.unitKey !== unitKey,
  );
  return messageIds.length
    ? [...otherUnits, { unitKey, messageIds }]
    : otherUnits;
};

const applyReplyStateToEntry = (
  entry: InboxEntry,
  payload: InboxEntryReplyStatePayload,
): InboxEntry => {
  const communications = entry.communications?.map((comm) =>
    comm.threadMessageId === payload.threadMessageId &&
    isEmailCommunication(comm)
      ? {
          ...comm,
          labelIds: withUnread(comm.labelIds, payload.threadHasUnread),
        }
      : comm,
  );

  return {
    ...entry,
    newReplies: withUnitNewReplies(
      entry.newReplies,
      payload.threadMessageId,
      payload.newReplyMessageIds,
    ),
    ...(communications ? { communications } : {}),
  };
};

type HeldEntries = Pick<
  InboxEntriesHandlerSlice,
  'inboxEntries' | 'modalInboxEntry'
>;

const findHeldEntry = (
  state: HeldEntries,
  inboxEntryId: string,
): InboxEntry | undefined =>
  state.inboxEntries.find((entry) => entry.id === inboxEntryId) ??
  (state.modalInboxEntry?.id === inboxEntryId
    ? state.modalInboxEntry
    : undefined);

const isEntryHeld = (state: HeldEntries, inboxEntryId: string): boolean =>
  !!findHeldEntry(state, inboxEntryId);

const updateHeldEntry = (
  set: ZustandSet,
  inboxEntryId: string,
  transform: (entry: InboxEntry) => InboxEntry,
) => {
  set((state) =>
    isEntryHeld(state, inboxEntryId)
      ? mapHeldEntries(state, inboxEntryId, transform)
      : state,
  );
};

export const handleApplyInboxEntryReplyState = (
  set: ZustandSet,
  payload: InboxEntryReplyStatePayload,
) => {
  updateHeldEntry(set, payload.inboxEntryId, (entry) =>
    applyReplyStateToEntry(entry, payload),
  );
};

const clearUnitFromEntry = (
  entry: InboxEntry,
  unitKey: string,
): InboxEntry => ({
  ...entry,
  ...(entry.newReplies
    ? {
        newReplies: entry.newReplies.filter(
          (reply) => reply.unitKey !== unitKey,
        ),
      }
    : {}),
  ...(entry.chatActivity
    ? {
        chatActivity: entry.chatActivity.map((unit) =>
          unit.unitKey === unitKey ? { ...unit, newReplyMessageIds: [] } : unit,
        ),
      }
    : {}),
});

export const handleClearUnitNewReply = (
  set: ZustandSet,
  inboxEntryId: string,
  unitKey: string,
) => {
  updateHeldEntry(set, inboxEntryId, (entry) =>
    clearUnitFromEntry(entry, unitKey),
  );
};

const withoutIds = (ids: string[], dropped: ReadonlySet<string>) =>
  ids.filter((id) => !dropped.has(id));

const dropDismissedFromEntry = (
  entry: InboxEntry,
  dismissed: ReadonlySet<string>,
): InboxEntry => ({
  ...entry,
  ...(entry.newReplies
    ? { newReplies: dropNewReplyMessageIds(entry.newReplies, dismissed) }
    : {}),
  ...(entry.chatActivity
    ? {
        chatActivity: entry.chatActivity.map((unit) => ({
          ...unit,
          newReplyMessageIds: withoutIds(unit.newReplyMessageIds, dismissed),
        })),
      }
    : {}),
});

// Optimistic. Maps each array it writes into a held copy to the one it replaced, copy by copy, so
// the rollback and the server's answer only touch a copy nothing has rewritten since.
export const handleDismissEntryNewReplies = (
  set: ZustandSet,
  get: ZustandGet,
  inboxEntryId: string,
  messageIds: string[],
): DismissedRepliesSnapshot | null => {
  if (!isEntryHeld(get(), inboxEntryId)) {
    return null;
  }

  const dismissed = new Set(messageIds);
  const newReplies: DismissedRepliesSnapshot['newReplies'] = new Map();
  const chatActivity: DismissedRepliesSnapshot['chatActivity'] = new Map();
  updateHeldEntry(set, inboxEntryId, (entry) => {
    const next = dropDismissedFromEntry(entry, dismissed);
    if (next.newReplies) {
      newReplies.set(next.newReplies, entry.newReplies);
    }
    if (next.chatActivity) {
      chatActivity.set(next.chatActivity, entry.chatActivity);
    }
    return next;
  });
  return { inboxEntryId, newReplies, chatActivity };
};

// A copy whose array was rewritten since holds server state: the failed dismiss never landed.
const restoreDismissedOnEntry = (
  entry: InboxEntry,
  { newReplies, chatActivity }: DismissedRepliesSnapshot,
): InboxEntry => ({
  ...entry,
  ...(entry.newReplies &&
    newReplies.has(entry.newReplies) && {
      newReplies: newReplies.get(entry.newReplies),
    }),
  ...(entry.chatActivity &&
    chatActivity.has(entry.chatActivity) && {
      chatActivity: chatActivity.get(entry.chatActivity),
    }),
});

export const handleRestoreDismissedReplies = (
  set: ZustandSet,
  snapshot: DismissedRepliesSnapshot,
) => {
  updateHeldEntry(set, snapshot.inboxEntryId, (entry) =>
    restoreDismissedOnEntry(entry, snapshot),
  );
};

// A frame applied while the dismiss was in flight is newer than the answer, so it stays.
export const handleSettleDismissedReplies = (
  set: ZustandSet,
  snapshot: DismissedRepliesSnapshot,
  newReplies: InboxEntryNewReply[],
) => {
  updateHeldEntry(set, snapshot.inboxEntryId, (entry) =>
    entry.newReplies && snapshot.newReplies.has(entry.newReplies)
      ? { ...entry, newReplies }
      : entry,
  );
};

// Replace only: a thread the entry does not already show is not linked to it.
const replaceEmailThreadHead = (
  communications: Partial<UserCommunication>[] | undefined,
  head: Partial<UserCommunication>,
): Partial<UserCommunication>[] | undefined => {
  const index = (communications ?? []).findIndex(
    (comm) =>
      isEmailCommunication(comm) &&
      comm.threadMessageId === head.threadMessageId &&
      comm.userId === head.userId,
  );
  return index === -1
    ? undefined
    : communications?.map((comm, i) => (i === index ? head : comm));
};

export const handleUpsertEmailThreadHeadInInboxEntries = (
  set: ZustandSet,
  head: Partial<UserCommunication>,
) => {
  if (!head.threadMessageId || !isEmailCommunication(head)) {
    return;
  }
  const ucSnapshot = useUserCommunicationsStore.getState();
  const now = Date.now();

  set((state) =>
    mapHeldEntries(
      state,
      () => true,
      (entry) => {
        const incoming = replaceEmailThreadHead(entry.communications, head);
        if (!incoming) {
          return entry;
        }
        return {
          ...entry,
          communications: reconcileEntryCommunications({
            incoming,
            previousLocal: entry.communications,
            unconfirmedDrafts: ucSnapshot.unconfirmedDrafts,
            ucDraftRowsForEntry: collectFreshUnconfirmedDraftRowsForEntry(
              ucSnapshot,
              entry.id,
              now,
            ),
            now,
          }) as UserCommunication[],
        };
      },
    ),
  );
};

export const handleGetCommunicationsInInboxEntry = (
  get: ZustandGet,
  inboxEntryId: string,
) =>
  get().inboxEntries.find((inboxEntry) => inboxEntry.id === inboxEntryId)
    ?.communications as UserCommunication[];

export const handleSetArchiveLoading = (
  set: ZustandSet,
  isArchiveLoading: boolean,
) => {
  set({ isArchiveLoading });
};

export const handleUpsertDraftInInboxEntry = (
  set: ZustandSet,
  inboxEntryId: string,
  draftCommunication: Partial<UserCommunication>,
  messageId?: string,
) => {
  set((state) => {
    const inboxEntry = state.inboxEntries.find((e) => e.id === inboxEntryId);
    const slotMatches = state.modalInboxEntry?.id === inboxEntryId;

    // No-op when the entry isn't mounted (e.g. replying from the Inbox
    // preview while DoBox isn't open) — reconcileEntryCommunications' own
    // thread substitution is what catches up once the entry is next fetched.
    if (!inboxEntry && !slotMatches) {
      return state;
    }

    let updatedInboxEntries = state.inboxEntries;

    if (inboxEntry) {
      const updatedCommunications = upsertThreadHeadCommunication(
        inboxEntry.communications ?? [],
        draftCommunication,
        messageId,
      );

      const updatedInboxEntry = {
        ...inboxEntry,
        communications: updatedCommunications as UserCommunication[],
        communicationIds: buildCommunicationIds(updatedCommunications),
      };

      updatedInboxEntries = state.inboxEntries.map((entry) =>
        entry.id === inboxEntryId ? updatedInboxEntry : entry,
      );
    }

    let updatedModalEntry = state.modalInboxEntry;

    if (slotMatches) {
      const slotComms = upsertThreadHeadCommunication(
        state.modalInboxEntry!.communications ?? [],
        draftCommunication,
        messageId,
      );
      updatedModalEntry = {
        ...state.modalInboxEntry!,
        communications: slotComms as UserCommunication[],
        communicationIds: buildCommunicationIds(slotComms),
      };
    }

    return {
      ...state,
      inboxEntries: updatedInboxEntries,
      modalInboxEntry: updatedModalEntry,
    };
  });
};

export const handleModifyInboxEntryUserCommunication = (
  set: ZustandSet,
  inboxEntryId: string,
  userCommunication: Partial<UserCommunication>,
) => {
  set((state) => {
    const inboxEntry = state.inboxEntries.find((e) => e.id === inboxEntryId);
    const slotMatches = state.modalInboxEntry?.id === inboxEntryId;

    if (!inboxEntry && !slotMatches) {
      return state;
    }

    const updater = buildCommunicationUpdater(userCommunication);

    let updatedInboxEntries = state.inboxEntries;

    if (inboxEntry) {
      const communications = inboxEntry.communications ?? [];
      const updatedCommunications = communications.map(updater);
      const updatedCommunicationIds = updatedCommunications
        .map((c) => c.messageId || c.draftId)
        .filter(Boolean) as string[];

      const updatedInboxEntry = {
        ...inboxEntry,
        communications: updatedCommunications as UserCommunication[],
        communicationIds: updatedCommunicationIds,
      };

      updatedInboxEntries = state.inboxEntries.map((entry) =>
        entry.id === inboxEntryId ? updatedInboxEntry : entry,
      );
    }

    let updatedModalEntry = state.modalInboxEntry;

    if (slotMatches) {
      const slotComms = (state.modalInboxEntry!.communications ?? []).map(
        updater,
      );
      updatedModalEntry = {
        ...state.modalInboxEntry!,
        communications: slotComms as UserCommunication[],
        communicationIds: slotComms
          .map((c) => c.messageId || c.draftId)
          .filter(Boolean) as string[],
      };
    }

    return {
      ...state,
      inboxEntries: updatedInboxEntries,
      modalInboxEntry: updatedModalEntry,
    };
  });
};

const communicationsContainDraft = (
  communications: Partial<UserCommunication>[] | undefined,
  draftId: string,
): boolean =>
  !!communications?.some(
    (comm) => comm.messageId === draftId || rowCarriesDraft(comm, draftId),
  );

// A row matched by draftId/messageId is either the server-shape thread head
// (restore its remaining thread messages) or a local-shape draft row with no
// thread of its own (drop it — see the dedupe note below).
const restoreThreadForRemovedDraft = (
  row: Partial<UserCommunication>,
  draftId: string,
  otherRows: Partial<UserCommunication>[],
): Partial<UserCommunication> | null => {
  const restored = buildThreadRestoreUserCommunication(row, draftId);
  if (!restored) {
    return null;
  }

  // Cheap dedupe against a physically present original: old persisted state
  // may still hold the pre-refactor shape (the draft as its own row, with the
  // original present separately) — inserting the restored head here would
  // duplicate that row, so drop it instead.
  const isDuplicate = otherRows.some(
    (other) => other.messageId === restored.messageId,
  );
  return isDuplicate ? null : restored;
};

const removeDraftFromCommunications = (
  communications: Partial<UserCommunication>[],
  draftId: string,
): Partial<UserCommunication>[] => {
  const otherRows = communications.filter(
    (comm) => comm.draftId !== draftId && comm.messageId !== draftId,
  );

  return communications
    .map((comm) =>
      comm.draftId === draftId || comm.messageId === draftId
        ? restoreThreadForRemovedDraft(comm, draftId, otherRows)
        : stripThreadCarriedDraft(comm, draftId),
    )
    .filter((comm): comm is Partial<UserCommunication> => comm !== null);
};

const rewriteEntryForDraftRemoval = (
  inboxEntry: InboxEntry,
  draftId: string,
): InboxEntry => {
  if (!inboxEntry.communications) {
    return inboxEntry;
  }
  const communications = removeDraftFromCommunications(
    inboxEntry.communications,
    draftId,
  ) as UserCommunication[];
  return {
    ...inboxEntry,
    communications,
    communicationIds: buildCommunicationIds(communications),
  };
};

// The entry survives even at zero communications, mirroring
// handleUnlinkUserCommunicationsFromInboxEntry — this only strips the draft,
// never deletes the entry.
export const handleRemoveDraftFromInboxEntries = (
  set: ZustandSet,
  draftId: string,
) => {
  set((state) => {
    const entriesNeedUpdate = state.inboxEntries.some((entry) =>
      communicationsContainDraft(entry.communications, draftId),
    );
    const modalNeedsUpdate = communicationsContainDraft(
      state.modalInboxEntry?.communications,
      draftId,
    );

    if (!entriesNeedUpdate && !modalNeedsUpdate) {
      return state;
    }

    const inboxEntries = entriesNeedUpdate
      ? state.inboxEntries.map((entry) =>
          rewriteEntryForDraftRemoval(entry, draftId),
        )
      : state.inboxEntries;

    const modalInboxEntry =
      modalNeedsUpdate && state.modalInboxEntry
        ? rewriteEntryForDraftRemoval(state.modalInboxEntry, draftId)
        : state.modalInboxEntry;

    return {
      ...state,
      inboxEntries,
      modalInboxEntry,
    };
  });
};

export const handleSetModalInboxEntry = (
  set: ZustandSet,
  modalInboxEntry: InboxEntry | null,
) => {
  set({ modalInboxEntry });
};
